import { useCallback, useEffect, useState } from 'react'

import { useFetch, validateEmail } from '@reveni-io/reveni-ui'

import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'

import { servicesByConfigurationMode } from './constants'
import { transformNotificationTypes } from './helpers'

const useCommunicationsConfiguration = ({ storeInfo, configurationMode, merchantId, returnPortalId }) => {
  const services = servicesByConfigurationMode[configurationMode || configurationTypes.returnsPortal]
  const entityId = getEntityIdByConfigurationMode({ configurationMode, merchantId, returnPortalId })
  const {
    data: communicationsPreferences,
    loading: gettingCommunicationsPreferences,
    fetchData: refetchCommunicationsPreferences,
  } = useFetch(services.getCommunicationsPreferences, {
    doInitialRequest: true,
    params: entityId,
  })
  const { fetchData: editCommunicationsPreferences, loading: applyingCommunicationsPreferences } = useFetch(
    services.editCommunicationsPreferences,
    {
      doInitialRequest: false,
    }
  )

  const [replyToEmail, setReplyToEmail] = useState('')
  const [replyToEmailType, setReplyToEmailType] = useState('')
  const [notificationTypes, setNotificationTypes] = useState([])
  const [billingEmail, setBillingEmail] = useState('')
  const [billingCcEmails, setBillingCcEmails] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [isValidBillingEmail, setIsValidBillingEmail] = useState(false)
  const [language, setLanguage] = useState(null)
  const [currentMode, setCurrentMode] = useState(configurationMode)

  const setInitValues = useCallback(() => {
    if (communicationsPreferences) {
      setReplyToEmail(communicationsPreferences?.reply_to_email || storeInfo?.contact_email)
      setReplyToEmailType(communicationsPreferences?.reply_to_type)
      setNotificationTypes(transformNotificationTypes(communicationsPreferences?.notification_types))
      setLanguage(communicationsPreferences?.language)
      setBillingEmail(communicationsPreferences?.billing_email)
      setIsValidBillingEmail(validateEmail(communicationsPreferences?.billing_email))
      setBillingCcEmails(communicationsPreferences?.billing_cc_emails || [])
      setHasChanges(false)
    }
  }, [communicationsPreferences, storeInfo])

  useEffect(() => {
    setInitValues()
  }, [communicationsPreferences, setInitValues])

  useEffect(() => {
    if (currentMode && configurationMode !== currentMode) {
      refetchCommunicationsPreferences(entityId)
    }
    setCurrentMode(configurationMode)
  }, [configurationMode, currentMode, entityId, refetchCommunicationsPreferences])

  const revertChanges = () => {
    setCurrentMode(configurationMode)
    setInitValues()
  }

  const handleChangeRecipientEmail = (identifier, email) => {
    const newNotificationTypes = notificationTypes?.map(notification => {
      if (notification.identifier === identifier) {
        return { ...notification, email }
      }

      return notification
    })

    setHasChanges(true)
    setNotificationTypes(newNotificationTypes)
  }

  const handleChangeTransactionalCommunications = (identifier, isEnabled) => {
    const newNotificationTypes = notificationTypes?.map(notification => {
      if (notification.identifier === identifier) {
        return { ...notification, email_enabled: isEnabled }
      }

      return notification
    })
    setHasChanges(true)
    setNotificationTypes(newNotificationTypes)
  }

  const handleChangeCcEmails = (identifier, ccEmails) => {
    const newNotificationTypes = notificationTypes?.map(notification => {
      if (notification.identifier === identifier) {
        return { ...notification, cc_emails: ccEmails }
      }

      return notification
    })

    setHasChanges(true)
    setNotificationTypes(newNotificationTypes)
  }

  const onChangeBillingEmail = (e, isValid) => {
    setHasChanges(true)
    setIsValidBillingEmail(isValid)
    setBillingEmail(e.target.value)
  }

  const onChangeBillingCcEmails = value => {
    setHasChanges(true)
    setBillingCcEmails(value)
  }

  return {
    communicationsPreferences,
    gettingCommunicationsPreferences,
    refetchCommunicationsPreferences,
    replyToEmail,
    setReplyToEmail: param => {
      setHasChanges(true)
      setReplyToEmail(param)
    },
    replyToEmailType,
    setReplyToEmailType: param => {
      setHasChanges(true)
      setReplyToEmailType(param)
    },
    editCommunicationsPreferences,
    applyingCommunicationsPreferences,
    notificationTypes,
    setNotificationTypes: param => {
      setHasChanges(true)
      setNotificationTypes(param)
    },
    hasChanges,
    setHasChanges,
    revertChanges,
    handleChangeRecipientEmail,
    handleChangeTransactionalCommunications,
    handleChangeCcEmails,
    language,
    setLanguage: param => {
      setHasChanges(true)
      setLanguage(param)
    },
    billingEmail,
    onChangeBillingEmail,
    billingCcEmails,
    onChangeBillingCcEmails,
    isValidBillingEmail,
    enabledPortalsWithGlobal: communicationsPreferences?.enabled_portals || [],
  }
}

export default useCommunicationsConfiguration
